import React, { useCallback, useEffect, useState } from "react";
import PageTableHeader from "../../../components/pageTableHeader/pageTableHeader";
import CommonTable from "../../../components/table/table";
import DefaultButton from "../../../components/buttons/defaultButton";
import toast from "react-hot-toast";
import {
  downloadReportFromHistory,
  getExportHistory,
} from "../../../../services/workspace/reports.service";

const breadcrumbs = [
  { label: "Reports", path: "/reports" },
  { label: "Export History", path: "" },
];

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const pickStatus = (row) =>
  String(row?.status || row?.export_status || "PENDING").toUpperCase();

const pickFileName = (row, id) =>
  row?.file_name ||
  row?.filename ||
  row?.name ||
  `report-export-${id || "file"}.xlsx`;

const pickId = (row) => row?.id || row?.uuid || row?.export_id;

const ExportHistory = () => {
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalData, setTotalData] = useState(0);
  const [rows, setRows] = useState([]);

  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getExportHistory({ page: currentPage });
      const list = response?.data?.data || response?.data || [];
      const meta = response?.data?.meta || response?.meta || {};
      setRows(Array.isArray(list) ? list : []);
      setTotalData(
        Number(meta?.total) || (Array.isArray(list) ? list.length : 0)
      );
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to load export history");
      setRows([]);
      setTotalData(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleDownload = async (row) => {
    const exportId = pickId(row);
    if (!exportId) {
      toast.error("Invalid export entry");
      return;
    }

    try {
      setDownloadingId(exportId);
      const blobData = await downloadReportFromHistory(exportId);
      const blob =
        blobData instanceof Blob
          ? blobData
          : new Blob([blobData], {
              type: "application/octet-stream",
            });

      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = pickFileName(row, exportId);
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to download report");
    } finally {
      setDownloadingId(null);
    }
  };

  const tableHeaders = [
    { label: "Report", key: "report_name" },
    { label: "Type", key: "type" },
    { label: "Status", key: "status" },
    { label: "Requested By", key: "requested_by" },
    { label: "Created At", key: "created_at" },
    { label: "Actions", key: "actions" },
  ];

  const tableData = rows.map((row) => {
    const id = pickId(row);
    const status = pickStatus(row);
    const canDownload =
      status === "COMPLETED" || status === "SUCCESS" || status === "DONE";

    return {
      id,
      report_name: {
        data: row?.report_name || row?.report || row?.module || "-",
        variant: "text",
      },
      type: {
        data: String(row?.format || row?.type || row?.file_type || "-").toUpperCase(),
        variant: "text",
      },
      status: {
        data: status.replaceAll("_", " "),
        variant: "text",
      },
      requested_by: {
        data:
          row?.requested_by?.name ||
          row?.requested_by_name ||
          row?.user?.name ||
          row?.created_by?.name ||
          "-",
        variant: "text",
      },
      created_at: {
        data: formatDateTime(row?.created_at || row?.createdAt),
        variant: "text",
      },
      actions: {
        data: canDownload ? (
          <DefaultButton
            variant="outline"
            name={downloadingId === id ? "Downloading..." : "Download"}
            disabled={downloadingId === id}
            onClick={() => handleDownload(row)}
          />
        ) : (
          <span className="text-xs text-gray-500">Not ready</span>
        ),
        variant: "text",
      },
    };
  });

  return (
    <div className="flex flex-col gap-4">
      <PageTableHeader breadcrumbs={breadcrumbs} isSearch={false} filter={false} shorting={false} />
      <div className="bg-white p-5 rounded-xl border border-borderColor">
        <CommonTable
          TableHeaderData={tableHeaders}
          tabledata={tableData}
          currentPage={currentPage}
          totalData={totalData}
          setCurrentPage={setCurrentPage}
          loading={loading}
          action={{}}
        />
      </div>
    </div>
  );
};

export default ExportHistory;
